import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import RemoveScroll from '../RemoveScroll/RemoveScroll';
import { SlidingPanel, PanelLink } from '../SlidingPanel';
import SlidingPanelsProvider from '../SlidingPanel/SlidingPanel.context';
import { GiftMessageCardAdd } from '../GiftMessageCard/GiftMessageCard';
import GiftMessageForm from '../GiftMessageForm/GiftMessageForm';
import { trackEvent } from '../../util/track';
import * as Styled from './GiftOptionsDialog.styled';
import { GET_USER_LOCALE } from '../App.gql';

/**
 * A `GiftOptionsDialog` showcases a Gift Message and Upsell collection of products
 */
const GiftOptionsDialog = ({
  upsellCollectionId,
  upsellTitle,
  upsellSubtitle,
  isOpen,
  onCloseClick,
  ...props
}) => {
  const { data } = useQuery(GET_USER_LOCALE);
  const country = data?.user?.locale?.country;

  return (
    <Styled.Modal
      isOpen={isOpen}
      onRequestClose={onCloseClick}
      showHeaderCloseButton={false}
      withPadding={false}
      hasHeaderDivider={false}
      withCollapse
    >
      <Styled.Container data-track-region="gift options modal">
        <RemoveScroll>
          <SlidingPanelsProvider isOpen onEveryClose={onCloseClick}>
            <SlidingPanel isRoot panelId="gift-options" title="Make It A Gift">
              <Styled.PanelContainer>
                <PanelLink
                  forPanelId="gift-message"
                  onClick={() => {
                    trackEvent({ event: 'gift_message_prompt' });
                  }}
                >
                  <GiftMessageCardAdd withChevron />
                </PanelLink>
                {country === 'US' && (
                  <Styled.UpsellCollection
                    heading={upsellTitle}
                    subtitle={upsellSubtitle}
                    id={upsellCollectionId}
                    onComplete={onCloseClick}
                    {...props}
                  />
                )}
              </Styled.PanelContainer>
            </SlidingPanel>

            <SlidingPanel panelId="gift-message" title="Gift Message">
              <GiftMessageForm
                onComplete={onCloseClick}
                onReject={onCloseClick}
                {...props}
              />
            </SlidingPanel>
          </SlidingPanelsProvider>
        </RemoveScroll>
      </Styled.Container>
    </Styled.Modal>
  );
};

GiftOptionsDialog.propTypes = {
  /**
   * `true` to display the gift options dialog
   */
  isOpen: PropTypes.bool,
  upsellCollectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  upsellTitle: PropTypes.string,
  upsellSubtitle: PropTypes.string,
  /**
   * callback function to close the modal
   */
  onCloseClick: PropTypes.func.isRequired
};

GiftOptionsDialog.defaultProps = {
  isOpen: false,
  upsellCollectionId: null,
  upsellTitle: null,
  upsellSubtitle: null
};

export default GiftOptionsDialog;
